import React, { useEffect } from 'react';
import { ChevronDown, Check, CheckCircle, ShieldCheck, Clock, Zap, Heart, AlertCircle } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
import { initPixel, trackEvent } from '../utils/metaPixel';
// Image URLs
const MAIN_PRODUCT_IMG = 'https://cdn.magicpatterns.com/uploads/ifJJ9qTesMFLL53oHpiPeQ/Mockup_del_producto_principal.png';
const BONUS_1_IMG = 'https://cdn.magicpatterns.com/uploads/uyA25vu7xSpbsCZ6CTfDYY/Mockup_del_bono_1.png';
const BONUS_2_IMG = 'https://cdn.magicpatterns.com/uploads/exn9QHgHAcJ4EhksX5Xjcp/Mockup_del_bono_2.png';
const BONUS_3_IMG = 'https://cdn.magicpatterns.com/uploads/hAfNZZANY9MyvPdJyQVVHd/Mockup_del_bono_3.png';
const CHECKOUT_URL = 'https://checkout.tumundoglobalprime.online/cart/52720785457455:1';
export function LandingPage() {
  useEffect(() => {
    // Initialize Meta Pixel (includes automatic PageView tracking)
    initPixel();
    // Track scroll depth
    let scrollTracked = {
      '25': false,
      '50': false,
      '75': false,
      '100': false
    };
    const handleScroll = () => {
      const scrollPercent = (window.scrollY + window.innerHeight) / document.body.scrollHeight * 100;
      if (scrollPercent > 25 && !scrollTracked['25']) {
        trackEvent('ScrollDepth', {
          depth: '25%'
        });
        scrollTracked['25'] = true;
      }
      if (scrollPercent > 50 && !scrollTracked['50']) {
        trackEvent('ScrollDepth', {
          depth: '50%'
        });
        scrollTracked['50'] = true;
      }
      if (scrollPercent > 75 && !scrollTracked['75']) {
        trackEvent('ScrollDepth', {
          depth: '75%'
        });
        scrollTracked['75'] = true;
      }
      if (scrollPercent > 99 && !scrollTracked['100']) {
        trackEvent('ScrollDepth', {
          depth: '100%'
        });
        scrollTracked['100'] = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToCheckout = () => {
    const checkoutSection = document.getElementById('checkout');
    if (checkoutSection) {
      checkoutSection.scrollIntoView({
        behavior: 'smooth'
      });
      trackEvent('InitiateCheckout', {
        method: 'scroll_button'
      });
    }
  };
  const handleBuyClick = () => {
    trackEvent('Purchase', {
      value: 19990,
      currency: 'ARS'
    });
    window.location.href = CHECKOUT_URL;
  };
  return <div className="min-h-screen bg-[#FFF8F0] font-sans text-gray-800 overflow-x-hidden" data-id="element-20">
      <PurchasePopup data-id="element-21" />

      {/* 1. PROMISE SECTION */}
      <section className="relative py-16 md:py-24 px-4 md:px-6 lg:px-8" data-id="element-22">
        <div className="max-w-7xl mx-auto" data-id="element-23">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-24">
            {/* Left Column - Content */}
            <div className="space-y-6" data-id="element-25">
              <div className="inline-block px-4 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-bold" data-id="element-26">
                MÉTODO COMPROBADO
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight" data-id="element-27">
                Tu cuerpo sí puede{' '}
                <span className="text-[#FF6B6B]" data-id="element-28">cambiar</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-700 leading-relaxed" data-id="element-29">
                Transforma tu cuerpo desde casa en{' '}
                <span className="font-bold" data-id="element-30">14 días</span>, sin gimnasio, sin
                equipos caros y con rutinas de solo{' '}
                <span className="font-bold" data-id="element-31">10–25 minutos al día</span>.
              </p>

              <button onClick={scrollToCheckout} className="bg-[#FF6B6B] hover:bg-[#ff5252] text-white text-lg md:text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 inline-flex items-center gap-2" data-id="element-32">
                SÍ, QUIERO EMPEZAR HOY
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-33">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-34" />
                </svg>
              </button>
            </div>

            {/* Right Column - Product Image */}
            <div className="flex justify-center md:justify-end" data-id="element-35">
              <img src={MAIN_PRODUCT_IMG} alt="Fitness Desde Casa Sin Gym Libro Digital" className="w-full max-w-md cursor-pointer hover:scale-105 transition-transform duration-300 drop-shadow-2xl" onClick={scrollToCheckout} data-id="element-36" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. PROBLEMS SECTION */}
      <section className="py-20 bg-white px-4 md:px-6" data-id="element-37">
        <div className="max-w-3xl mx-auto" data-id="element-38">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-39">
            Seamos honestos…
          </h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-40">
            <p data-id="element-41">
              Has empezado muchas veces. Con ganas. Con ilusión. Con promesas.
            </p>
            <p data-id="element-42">Y casi siempre termina igual:</p>
            <div className="bg-red-50 p-6 rounded-xl border-l-4 border-red-400 my-8" data-id="element-43">
              <p className="italic" data-id="element-44">
                Te miras al espejo y no te gusta lo que ves. La ropa aprieta o
                ya no entra. Evitas fotos. Te cansas rápido. Te duele la
                espalda, las rodillas o la cintura. Te falta energía. Abandonas
                otra vez. Te culpas. Piensas: "no sirvo para esto".
              </p>
            </div>
            <p data-id="element-45">Y lo peor no es el cuerpo… Es lo que te dices por dentro:</p>
            <p className="font-medium text-gray-900" data-id="element-46">
              "Nunca voy a cambiar." "Cada año estoy peor." "Ya es tarde para
              mí." "No tengo disciplina."
            </p>
            <p className="mt-8" data-id="element-47">
              Pero aquí está la verdad que casi nadie te dice:
            </p>
            <p className="text-xl font-bold text-teal-700" data-id="element-48">
              El problema no eres tú. El problema es que te dieron métodos
              diseñados para personas con tiempo, dinero y vidas perfectas.
            </p>
            <p data-id="element-49">
              Tú tienes trabajo. Familia. Cansancio. Responsabilidades. <br data-id="element-50" />
              <span className="font-bold" data-id="element-51">Tienes una vida real.</span>
            </p>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION */}
      <section className="py-20 bg-[#FFF8F0] px-4 md:px-6" data-id="element-52">
        <div className="max-w-4xl mx-auto text-center" data-id="element-53">
          <p className="text-teal-600 font-bold tracking-wide uppercase mb-4" data-id="element-54">
            La Solución Definitiva
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-12" data-id="element-55">
            Por eso se creó: <br data-id="element-56" />
            <span className="text-[#FF6B6B]" data-id="element-57">FITNESS DESDE CASA SIN GYM</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center text-left" data-id="element-58">
            <div className="order-2 md:order-1" data-id="element-59">
              <p className="text-lg mb-6" data-id="element-60">
                Un sistema paso a paso para transformar tu cuerpo:
              </p>
              <ul className="space-y-4" data-id="element-61">
                {['Desde tu casa', 'Sin gimnasio', 'Sin equipos', 'Con rutinas cortas (10 a 25 minutos)', 'Adaptadas a tu nivel', 'Diseñadas para personas reales'].map((item, i) => <li key={i} className="flex items-center bg-white p-3 rounded-lg shadow-sm" data-id="element-62">
                    <Check className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0" data-id="element-63" />
                    <span className="font-medium" data-id="element-64">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-65">
              <img src={MAIN_PRODUCT_IMG} alt="Fitness Program" className="w-full max-w-sm hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToCheckout} data-id="element-66" />
            </div>
          </div>

          <div className="mt-12 bg-white p-6 rounded-xl shadow-md inline-block" data-id="element-67">
            <p className="text-lg font-medium text-gray-800" data-id="element-68">
              No es una rutina suelta. No es un video más de YouTube. No es una
              app genérica. <br data-id="element-69" />
              <span className="text-[#FF6B6B] font-bold" data-id="element-70">
                Es un sistema completo de transformación corporal compatible con
                tu vida real.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* 4. REINFORCEMENT SECTION */}
      <section className="py-20 bg-white px-4 md:px-6" data-id="element-71">
        <div className="max-w-3xl mx-auto" data-id="element-72">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-73">
            Este método se basa en algo que casi nadie usa: <br data-id="element-74" />
            <span className="text-teal-600" data-id="element-75">
              Micro-hábitos físicos progresivos
            </span>
          </h2>

          <div className="prose prose-lg mx-auto text-gray-700" data-id="element-76">
            <p data-id="element-77">
              En lugar de exigirte cambios gigantes… Empiezas pequeño. Simple.
              Fácil. Como cepillarte los dientes.
            </p>

            <div className="grid grid-cols-2 gap-4 my-8" data-id="element-78">
              {[{
              icon: AlertCircle,
              text: 'No generan rechazo'
            }, {
              icon: Zap,
              text: 'No cansan mentalmente'
            }, {
              icon: Heart,
              text: 'No dependen de motivación'
            }, {
              icon: Clock,
              text: 'Se vuelven automáticas'
            }].map((item, i) => <div key={i} className="bg-gray-50 p-4 rounded-lg flex flex-col items-center text-center" data-id="element-79">
                  <item.icon className="w-8 h-8 text-[#FF6B6B] mb-2" data-id="element-80" />
                  <span className="font-medium text-sm md:text-base" data-id="element-81">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <p data-id="element-82">
              Poco a poco… Tu cuerpo cambia. Tu energía vuelve. Tu postura
              mejora. Tu ropa entra otra vez. Tu confianza regresa.
            </p>

            <div className="bg-teal-50 p-6 rounded-xl border border-teal-200 mt-8 text-center" data-id="element-83">
              <p className="mb-4" data-id="element-84">Y lo más importante: Pasas de pensar…</p>
              <p className="text-gray-500 line-through mb-2" data-id="element-85">
                "Intento entrenar"
              </p>
              <p className="text-2xl font-bold text-teal-800 mb-4" data-id="element-86">
                a: "Soy alguien que entrena."
              </p>
              <p className="text-sm font-medium" data-id="element-87">
                Sin pelear contigo mismo. Sin culpa. Sin sufrir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. BONUSES SECTION */}
      <section className="py-20 bg-[#FFF8F0] px-4 md:px-6" data-id="element-88">
        <div className="max-w-5xl mx-auto" data-id="element-89">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-90">
            Además del programa completo, recibes estos 3 bonos:
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-91">
            {/* Bonus 1 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:-translate-y-2 transition-transform duration-300" data-id="element-92">
              <div className="bg-[#FF6B6B] text-white text-center py-2 font-bold text-sm tracking-widest" data-id="element-93">
                GRATIS POR HOY
              </div>
              <div className="p-6 flex-grow flex flex-col items-center text-center" data-id="element-94">
                <img src={BONUS_1_IMG} alt="Protocolo Anti-Abandono" className="w-48 h-48 object-contain mb-4 cursor-pointer" onClick={scrollToCheckout} data-id="element-95" />
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-96">
                  Protocolo Anti-Abandono
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-97">
                  Un sistema mental y práctico para no rendirte cuando baje la
                  motivación, romper el patrón de abandono y mantener constancia
                  sin fuerza de voluntad.
                </p>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:-translate-y-2 transition-transform duration-300" data-id="element-98">
              <div className="bg-[#FF6B6B] text-white text-center py-2 font-bold text-sm tracking-widest" data-id="element-99">
                GRATIS POR HOY
              </div>
              <div className="p-6 flex-grow flex flex-col items-center text-center" data-id="element-100">
                <img src={BONUS_2_IMG} alt="Rutinas de Emergencia" className="w-48 h-48 object-contain mb-4 cursor-pointer" onClick={scrollToCheckout} data-id="element-101" />
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-102">
                  Rutinas de Emergencia (5 min)
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-103">
                  Para esos días caóticos donde no tienes tiempo. Rutinas ultra
                  cortas para no romper la cadena y seguir avanzando.
                </p>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col transform hover:-translate-y-2 transition-transform duration-300" data-id="element-104">
              <div className="bg-[#FF6B6B] text-white text-center py-2 font-bold text-sm tracking-widest" data-id="element-105">
                GRATIS POR HOY
              </div>
              <div className="p-6 flex-grow flex flex-col items-center text-center" data-id="element-106">
                <img src={BONUS_3_IMG} alt="Guía Alimentaria" className="w-48 h-48 object-contain mb-4 cursor-pointer" onClick={scrollToCheckout} data-id="element-107" />
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-108">
                  Guía Alimentaria Real
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-109">
                  Sin dietas extremas. Aprendes qué comer en la vida real, cómo
                  mejorar sin sufrir y cómo disfrutar sin culpa.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-110">
            <button onClick={scrollToCheckout} className="bg-teal-600 hover:bg-teal-700 text-white text-xl font-bold py-4 px-10 rounded-full shadow-lg transition-all duration-300" data-id="element-111">
              ACCEDER A LOS BONOS AHORA
            </button>
          </div>
        </div>
      </section>

      {/* 6. OFFER SECTION */}
      <section className="py-20 bg-white px-4 md:px-6" data-id="element-112">
        <div className="max-w-4xl mx-auto bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-200 shadow-inner" data-id="element-113">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10" data-id="element-114">
            Esto es exactamente lo que te llevas:
          </h2>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 mb-10" data-id="element-115">
            {['Programa completo FITNESS DESDE CASA', 'Rutinas progresivas paso a paso', 'Entrenamiento en casa sin equipos', 'Sistema de micro-hábitos', 'Psicología de constancia', 'Adaptaciones por nivel', 'Protocolo Anti-Abandono (Bono)', 'Rutinas de Emergencia 5 min (Bono)', 'Guía Alimentaria Real (Bono)', 'Acceso inmediato de por vida'].map((item, i) => <div key={i} className="flex items-center" data-id="element-116">
                <CheckCircle className="w-5 h-5 text-[#FF6B6B] mr-3 flex-shrink-0" data-id="element-117" />
                <span className="font-medium text-gray-800" data-id="element-118">{item}</span>
              </div>)}
          </div>

          <div className="text-center border-t border-gray-200 pt-8 mb-8" data-id="element-119">
            <p className="text-xl font-medium text-gray-600 mb-2" data-id="element-120">
              Todo diseñado para una sola cosa:
            </p>
            <p className="text-2xl md:text-3xl font-bold text-teal-700" data-id="element-121">
              Que tu cuerpo cambie sin que tu vida se vuelva un infierno.
            </p>
          </div>

          <div className="text-center pt-8 border-t border-gray-200" data-id="element-122">
            <p className="text-gray-500 text-lg mb-2 line-through" data-id="element-123">
              Precio Normal: $39,990 ARS
            </p>
            <p className="text-5xl md:text-6xl font-extrabold text-[#FF6B6B] tracking-tight mb-8" data-id="element-124">
              $19,990{' '}
              <span className="text-2xl text-gray-600 font-normal" data-id="element-125">ARS</span>
            </p>

            <button onClick={handleBuyClick} className="w-full bg-[#FF6B6B] hover:bg-[#ff5252] text-white text-xl md:text-2xl font-bold py-6 px-12 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 mb-6" data-id="element-126">
              QUIERO TRANSFORMAR MI CUERPO AHORA
            </button>

            <div className="flex justify-center items-center gap-4 text-sm text-gray-500" data-id="element-127">
              <span className="flex items-center" data-id="element-128">
                <ShieldCheck className="w-4 h-4 mr-1" data-id="element-129" /> Pago 100% Seguro
              </span>
              <span className="flex items-center" data-id="element-130">
                <Check className="w-4 h-4 mr-1" data-id="element-131" /> Acceso Inmediato
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 7. TESTIMONIALS SECTION */}
      <section className="py-20 bg-[#FFF8F0] px-4 md:px-6" data-id="element-132">
        <div className="max-w-6xl mx-auto" data-id="element-133">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-134">
            Historias reales de personas como tú
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-6" data-id="element-135">
            <TestimonialCard name="María González" location="Buenos Aires" age={35} content="Nunca pensé que 15 minutos harían diferencia. Siempre dejaba el gym al mes. Con esto llevo 3 meses sin fallar y ya me entra el pantalón que tenía guardado hace años." data-id="element-136" />
            <TestimonialCard name="Carlos Ramírez" location="Madrid" age={42} content="Por mi trabajo viajo mucho y no puedo pagar gimnasio. Las rutinas de emergencia me salvaron. Bajé 6 kilos en 2 meses sin sentir que estaba a dieta." data-id="element-137" />
            <TestimonialCard name="Laura Fernández" location="Bogotá" age={31} content="Lo mejor es que no te sientes culpable. Si un día no puedes, el protocolo anti-abandono te ayuda a volver sin drama. Mi energía cambió totalmente." data-id="element-138" />
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-139">
            <TestimonialCard name="Roberto Silva" location="Ciudad de México" age={38} content="Tengo sobrepeso y me daba vergüenza ir al gym. Empezar en casa con este método fue lo mejor. Los ejercicios se adaptan a lo que puedo hacer. Muy recomendado." data-id="element-140" />
            <TestimonialCard name="Ana Martínez" location="Barcelona" age={29} content="La guía de alimentación es súper realista. Nada de comidas raras. Aprendí a comer mejor y las rutinas cortas me quitaron el dolor de espalda por la oficina." data-id="element-141" />
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE SECTION */}
      <section className="py-20 bg-white px-4 md:px-6" data-id="element-142">
        <div className="max-w-3xl mx-auto text-center" data-id="element-143">
          <div className="inline-block p-4 bg-teal-50 rounded-full mb-6" data-id="element-144">
            <ShieldCheck className="w-16 h-16 text-teal-600" data-id="element-145" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-146">
            Pruébalo durante 7 días completos
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed" data-id="element-147">
            Si sientes que no es para ti, no se adapta a tu vida o no te
            convence, solicitas tu devolución.
            <br data-id="element-148" />
            <br data-id="element-149" />
            <span className="font-bold" data-id="element-150">
              Sin explicaciones. Sin preguntas incómodas. Sin riesgo.
            </span>
          </p>
          <p className="text-xl font-medium text-gray-900" data-id="element-151">Tú decides.</p>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-20 bg-gray-50 px-4 md:px-6" data-id="element-152">
        <div className="max-w-3xl mx-auto" data-id="element-153">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-154">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-155">
            {[{
            q: '¿Funciona si nunca entrené?',
            a: 'Sí. Está diseñado especialmente para principiantes y personas que siempre abandonaron.'
          }, {
            q: '¿Necesito equipos o pesas?',
            a: 'No. Todo se hace con tu propio cuerpo desde casa.'
          }, {
            q: '¿Cuánto tiempo por día necesito?',
            a: 'Entre 10 y 25 minutos. Incluso menos con las rutinas de emergencia.'
          }, {
            q: '¿Sirve si tengo sobrepeso?',
            a: 'Sí. El sistema se adapta a tu nivel actual.'
          }, {
            q: '¿Y si ya fracasé muchas veces?',
            a: 'Entonces este método es para ti. No se basa en motivación, se basa en diseño.'
          }, {
            q: '¿Cuándo veo resultados?',
            a: 'Muchas personas notan cambios en energía, ánimo y cuerpo en las primeras semanas si siguen el sistema.'
          }, {
            q: '¿Es solo para jóvenes?',
            a: 'No. Funciona para hombres y mujeres entre 25 y 50+.'
          }, {
            q: '¿Esto es otro programa milagro?',
            a: 'No. No promete magia. Promete un sistema realista que puedes mantener.'
          }].map((item, i) => <div key={i} className="bg-white rounded-lg shadow-sm p-6" data-id="element-156">
                <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-center" data-id="element-157">
                  <span className="text-[#FF6B6B] mr-2" data-id="element-158">?</span> {item.q}
                </h3>
                <p className="text-gray-700 ml-6" data-id="element-159">{item.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 py-12 text-center text-sm" data-id="element-160">
        <p data-id="element-161">© 2024 Fitness Desde Casa. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-162">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}